import { asObservationId, asOrderId, asProcedureId, type MemberId, type ProcedureId } from "@repo/domain";
import { z } from "zod";
import { unitSchema } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { orderSetIdSchema } from "./order-set.schemas";
import { patientDocumentIdSchema } from "./patient-document.schemas";
import { correctProcedureTrialInputSchema, recordProcedureTrialInputSchema } from "./procedure-trial.schemas";

const domainIdSchema = z.string().trim().min(1);

export const diagnosticProcedureIdSchema: z.ZodType<ProcedureId, z.ZodTypeDef, string> = z
  .string()
  .trim()
  .min(1)
  .transform(asProcedureId);

export const diagnosticProcedureIdentitySchema = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});

export const recordDiagnosticProcedureOutcomeInputSchema = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
  outcome: z.enum(["performed", "didNotPerform", "unableToComplete"]),
  notes: z.string().optional(),
});

export type RecordDiagnosticProcedureOutcomeInput = z.infer<typeof recordDiagnosticProcedureOutcomeInputSchema>;

/** Undo an `unableToComplete` outcome: the work is under way again and the order is outstanding again. */
export const reopenDiagnosticProcedureInputSchema = z.object({
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});
export type ReopenDiagnosticProcedureInput = z.infer<typeof reopenDiagnosticProcedureInputSchema>;

/** Undo a `didNotPerform` outcome: the record is struck and the order goes back to outstanding. */
export const retractDiagnosticProcedureDidNotPerformInputSchema = z.object({
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});
export type RetractDiagnosticProcedureDidNotPerformInput = z.infer<
  typeof retractDiagnosticProcedureDidNotPerformInputSchema
>;

export const diagnosticRecordedValueSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("Quantity"), value: z.number(), unit: unitSchema.optional() }),
  z.object({ kind: z.literal("Integer"), value: z.number().int() }),
  z.object({ kind: z.literal("Boolean"), value: z.boolean() }),
  z.object({
    kind: z.literal("Choice"),
    selected: z.array(z.string().trim().min(1)),
    freeText: z.string().optional(),
  }),
  z.object({ kind: z.literal("Text"), value: z.string() }),
]);

const diagnosticObservationFactsSchema = z.object({
  trialId: domainIdSchema.transform(asObservationId).optional(),
  observationId: newEntityIdSchema.transform(asObservationId),
  definitionId: z
    .string()
    .trim()
    .min(1)
    .transform((value) => value as MemberId),
  observedAt: z.string().trim().min(1),
  values: z
    .array(
      z.object({
        componentId: z
          .string()
          .trim()
          .min(1)
          .transform((value) => value as MemberId),
        value: diagnosticRecordedValueSchema,
      }),
    )
    .min(1),
  note: z.string().optional(),
  /**
   * The filed document this reading was read off, when it was not typed in.
   *
   * OPTIONAL WITH NO DEFAULT, and that matters most on a correction: the
   * domain keeps the link a correction does not name, and the card corrects on
   * any edit without knowing anything about documents. A default of `null`
   * here would reach the domain as a present value and erase the provenance
   * the first time somebody fixed a typo.
   */
  derivedFromDocumentId: patientDocumentIdSchema.optional(),
});

export const recordDiagnosticObservationInputSchema = diagnosticObservationFactsSchema.extend({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});
export type RecordDiagnosticObservationInput = z.infer<typeof recordDiagnosticObservationInputSchema>;

export const recordDiagnosticObservationsInputSchema = diagnosticProcedureIdentitySchema.extend({
  observations: z.array(diagnosticObservationFactsSchema).min(1),
});
export type RecordDiagnosticObservationsInput = z.infer<typeof recordDiagnosticObservationsInputSchema>;

export const correctDiagnosticObservationInputSchema = recordDiagnosticObservationInputSchema.extend({
  observationId: domainIdSchema.transform(asObservationId),
});
export type CorrectDiagnosticObservationInput = z.infer<typeof correctDiagnosticObservationInputSchema>;

export const setDiagnosticProcedureNotesInputSchema = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
  notes: z.string().optional(),
});
export type SetDiagnosticProcedureNotesInput = z.infer<typeof setDiagnosticProcedureNotesInputSchema>;

export const markDiagnosticObservationUnableToCaptureInputSchema = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
  definitionId: domainIdSchema.transform((value) => value as MemberId),
});
export type MarkDiagnosticObservationUnableToCaptureInput = z.infer<
  typeof markDiagnosticObservationUnableToCaptureInputSchema
>;
export const clearDiagnosticObservationUnableToCaptureInputSchema = markDiagnosticObservationUnableToCaptureInputSchema;
export type ClearDiagnosticObservationUnableToCaptureInput = z.infer<
  typeof clearDiagnosticObservationUnableToCaptureInputSchema
>;

export const retractDiagnosticObservationInputSchema = z.object({
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
  observationId: domainIdSchema.transform(asObservationId),
});
export type RetractDiagnosticObservationInput = z.infer<typeof retractDiagnosticObservationInputSchema>;

export const importDiagnosticDocumentInputSchema = diagnosticProcedureIdentitySchema
  .extend({
    trials: z.array(recordProcedureTrialInputSchema.omit({ procedureId: true, orderSetId: true, orderId: true })),
    /** All import changes share one commit, including reused trials and readings. */
    trialCorrections: z
      .array(correctProcedureTrialInputSchema.omit({ procedureId: true, orderSetId: true, orderId: true }))
      .optional(),
    observations: z.array(diagnosticObservationFactsSchema).optional(),
    observationCorrections: z
      .array(diagnosticObservationFactsSchema.extend({ observationId: domainIdSchema.transform(asObservationId) }))
      .optional(),
  })
  .refine(
    (input) =>
      input.trials.length +
        (input.trialCorrections?.length ?? 0) +
        (input.observations?.length ?? 0) +
        (input.observationCorrections?.length ?? 0) >
      0,
    "An import must include at least one change",
  );
export type ImportDiagnosticDocumentInput = z.infer<typeof importDiagnosticDocumentInputSchema>;

export const adjustDiagnosticProcedureTimesInputSchema = diagnosticProcedureIdentitySchema
  .omit({ procedureId: true })
  .extend({
    start: z.string().datetime({ offset: true }).optional(),
    end: z.string().datetime({ offset: true }).optional(),
  });
export type AdjustDiagnosticProcedureTimesInput = z.infer<typeof adjustDiagnosticProcedureTimesInputSchema>;
